import ticketIcon from "@/assets/icons/ticket.svg";
import homeIcon from "@/assets/icons/home.svg";
import hammerIcon from "@/assets/icons/hammer.svg";
import compassIcon from "@/assets/icons/compass.svg";
import adminIcon from "@/assets/icons/admin.svg";
import shopIcon from "@/assets/icons/shop.svg";
import reviewIcon from "@/assets/icons/review.svg";
import inviteIcon from "@/assets/icons/invite.svg";
import docsIcon from "@/assets/icons/docs.svg";
import type { PrivateUser } from "@/interfaces/user";
import type { ProgramConfig } from "@/interfaces/program";

export type NavItem = {
  name: string;
  link: string;
  icon: string;
  badge?: number;
};

export { ticketIcon };

// Single source of truth for navigation, shared by the desktop rail and the
// mobile tab bar so the two can never drift apart. Projects/Gallery appear when
// shipping-for-budget is on; Shop stays behind its own flag, since the ticket
// economy it belongs to is the part that is genuinely dormant.
export function navItemsFor(
  user: PrivateUser | undefined,
  program: ProgramConfig,
): NavItem[] {
  // Logged out, show the public surfaces: the event directory and the guides.
  if (!user) {
    const guest: NavItem[] = [];
    if (program.events_enabled) {
      guest.push({ name: "Events", link: "/events", icon: compassIcon });
    }
    if (!program.guides_locked) {
      guest.push({ name: "Guides", link: "/guides", icon: docsIcon });
    }
    return guest;
  }

  const items: NavItem[] = [{ name: "Home", link: "/home", icon: homeIcon }];

  if (program.events_enabled) {
    items.push({ name: "Events", link: "/events", icon: compassIcon });
    items.push({ name: "Organize", link: "/organize", icon: inviteIcon });
  }

  // Locked means HQ is still writing them. Admins keep the link, because
  // they're the ones doing the writing.
  if (!program.guides_locked || user.is_admin) {
    items.push({ name: "Guides", link: "/guides", icon: docsIcon });
  }

  if (program.projects_enabled) {
    items.push({
      name: "Projects",
      link: "/projects",
      icon: hammerIcon,
      badge: user.unread_project_notification_count,
    });
    items.push({ name: "Gallery", link: "/explore", icon: compassIcon });
  }

  if (program.shop_enabled) {
    items.push({ name: "Shop", link: "/shop", icon: shopIcon });
  }

  if (
    program.review_enabled &&
    (user.is_admin || user.is_reviewer || user.is_fulfiller)
  ) {
    items.push({ name: "Review", link: "/review", icon: reviewIcon });
  }

  // The site queue rides on hand-ins being open rather than on review_enabled,
  // which gates the dormant project economy. These are two different queues
  // reviewing two different things, and this is the one with events behind it.
  if (program.submissions_open && (user.is_admin || user.is_reviewer)) {
    items.push({ name: "Sites", link: "/site-review", icon: reviewIcon });
  }

  if (user.is_admin || user.is_fulfiller) {
    items.push({ name: "Admin", link: "/admin", icon: adminIcon });
  }

  return items;
}
